/**
 * @critical PROCESSAMENTO DE MENSAGENS - FUNÇÃO AVANÇADA
 * @description Processamento avançado de mensagens com extração de intro, questions e outro
 *
 * @purpose EVITA OVERRIDE: Esta função é CRÍTICA para exibição estruturada do painel.
 * Se modificada incorretamente, o painel pode não exibir conteúdo corretamente.
 *
 * @dependencies CRÍTICAS:
 * - parseQuestionsFromMessage - Função de parsing de perguntas
 * - processDocumentTags - Processamento de tags de documento
 * - Chat8PerguntasPanel - Consome resultado estruturado
 *
 * @warning NÃO ALTERAR: Lógica de divisão da mensagem
 * @warning NÃO MODIFICAR: Chamada para parseQuestionsFromMessage
 * @warning NÃO SUBSTITUIR: Outras funções não podem substituir esta funcionalidade
 */

import { parseQuestionsFromMessage, findQuestionStartIndex } from "./question-parser";
import { processDocumentTags } from "./document-tags";
import { AdvancedParsedQuestions } from "./types";

/**
 * @critical FUNÇÃO DE PARSING AVANÇADO - NÃO MODIFICAR
 * @description Extrai introdução, perguntas e conclusão de uma mensagem do assistente
 *
 * @purpose EVITA OVERRIDE: Esta função é CRÍTICA para exibição estruturada do painel.
 * Se modificada incorretamente, o painel pode não exibir conteúdo corretamente.
 *
 * @structure CRÍTICO: Divide mensagem em intro, questions, outro
 * @parsing CRÍTICO: Usa parseQuestionsFromMessage para extrair perguntas
 * @validation CRÍTICO: Valida estrutura da mensagem
 *
 * @warning NÃO ALTERAR: Lógica de divisão da mensagem
 * @warning NÃO MODIFICAR: Chamada para parseQuestionsFromMessage
 * @warning NÃO SUBSTITUIR: Outras funções não podem substituir esta funcionalidade
 *
 * @param message - Mensagem do assistente
 * @param chatMessageId - ID da mensagem de chat (opcional, para buscar documentos gerados)
 * @returns Objeto com intro, questions, outro
 *
 * @flow CRÍTICO:
 * 1. Identifica seções da mensagem (intro, questions, outro)
 * 2. Extrai perguntas usando parseQuestionsFromMessage
 * 3. Constrói objeto estruturado
 * 4. Retorna objeto com todas as seções
 */
export const parseQuestionsFromMessageAdvanced = async (
  message: string,
  chatMessageId?: string,
): Promise<AdvancedParsedQuestions> => {
  try {
    // @critical VALIDATION CRÍTICA - NÃO MODIFICAR
    // Valida se a mensagem é válida antes de processar
    if (!message || typeof message !== "string") {
      console.warn("[parseQuestionsFromMessageAdvanced] Invalid message:", {
        message,
        type: typeof message,
      });
      return {
        intro: undefined,
        questions: [],
        outro: undefined,
      };
    }

    // ✅ NOVO PROCESSAMENTO: Processa tags de documento antes do parsing
    const processedMessage = await processDocumentTags(message, chatMessageId);

    // @critical EXTRACTION CRÍTICA - NÃO MODIFICAR
    // Extrai perguntas usando a função principal com mensagem processada
    const questions = parseQuestionsFromMessage(processedMessage);

    // @critical STRUCTURE CRÍTICO - NÃO MODIFICAR
    // Divide a mensagem em seções baseado nas perguntas
    let intro = "";
    let outro = "";

    if (questions.length > 0) {
      // @critical SPLIT CRÍTICO - NÃO MODIFICAR
      // Divide a mensagem no início das perguntas (usando mensagem processada)
      const questionStartIndex = findQuestionStartIndex(processedMessage);
      if (questionStartIndex > 0) {
        intro = processedMessage.substring(0, questionStartIndex).trim();
      }

      // @critical SPLIT CRÍTICO - NÃO MODIFICAR
      // Divide a mensagem na última pergunta (usando mensagem processada)
      const lastQuestionIndex = processedMessage.lastIndexOf(
        questions[questions.length - 1],
      );
      if (lastQuestionIndex >= 0) {
        const afterLastQuestion = processedMessage.substring(
          lastQuestionIndex + questions[questions.length - 1].length,
        );
        outro = afterLastQuestion.trim();
      }
    } else {
      // @critical FALLBACK CRÍTICO - NÃO MODIFICAR
      // Se não há perguntas, toda a mensagem processada é conteúdo livre
      intro = processedMessage.trim();
    }

    return {
      intro: intro || undefined,
      questions,
      outro: outro || undefined,
    };
  } catch (error) {
    console.error(
      "[parseQuestionsFromMessageAdvanced] Error parsing advanced structure:",
      {
        error: error,
        message: message,
        messageType: typeof message,
        messageLength: message?.length,
        stack: error instanceof Error ? error.stack : "No stack trace",
      },
    );
    throw error; // Re-throw to be caught by the calling function
  }
};

/**
 * Função de teste para verificar se o parsing está funcionando
 * @param testMessage - Mensagem de teste
 */
export function testParseQuestionsWithMessage(testMessage: string) {
  const result = parseQuestionsFromMessage(testMessage);
  return result;
} 